import styles from './WorkflowGrid.module.css';

// Self-contained stroke icons (currentColor) - no external assets, matches the
// terminal/mono aesthetic used across the site.
const ICONS = {
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" />
    </svg>
  ),
  building: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="3" width="10" height="18" rx="1" /><path d="M14 8h5a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1h-5" />
      <path d="M7 7h1M7 11h1M7 15h1M11 7h0M11 11h0M11 15h0M17 12h0M17 16h0" />
    </svg>
  ),
  columns: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="1.5" /><path d="M3 9h18M9 4v16M15 4v16" />
    </svg>
  ),
  mailCheck: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h16a1 1 0 0 1 1 1v7" /><path d="M3 7v10a1 1 0 0 0 1 1h9" />
      <path d="M3.5 6.5 12 12l8.5-5.5" /><path d="m15 17 2 2 4-4" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3c2.5 2.5 3.5 6 3.5 9s-1 6.5-3.5 9c-2.5-2.5-3.5-6-3.5-9s1-6.5 3.5-9Z" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /><path d="M11 8v6M8 11h6" />
    </svg>
  ),
  signal: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 18v-4M9 18v-8M14 18v-6M19 18V6" /><path d="M3 8.5 8.5 5 13 8l6-4" />
    </svg>
  ),
  penMessage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.5 8.5 0 0 1-12.4 7.5L3 21l2-5.6A8.5 8.5 0 1 1 21 11.5Z" />
      <path d="M14.5 8.5 9 14l-.5 2 2-.5 5.5-5.5a1.06 1.06 0 0 0-1.5-1.5Z" />
    </svg>
  ),
};

const WORKFLOWS = [
  {
    icon: 'target',
    title: 'Contact hunt',
    body: 'Describe your buyer, get verified contacts. Every one is deliverability-checked and employment-confirmed before it reaches your list.',
  },
  {
    icon: 'building',
    title: 'Company-first hunt (Deep Hunt)',
    body: 'Start from the account, not the contact. RepBuddy finds companies matching your ICP, vets them, then pulls the right 1–5 people at each.',
  },
  {
    icon: 'columns',
    title: 'List enrichment',
    body: 'Drop in any CSV of companies. Get back headcount, revenue, industry, HQ, founding year, operating status, with your columns preserved, new ones appended.',
  },
  {
    icon: 'mailCheck',
    title: 'Email verification',
    body: 'Paste or upload old emails. Get back valid / invalid / risky buckets so you never torch your sender reputation on a dead list.',
  },
  {
    icon: 'globe',
    title: 'Website finder',
    body: 'Name-only list? RepBuddy finds and validates each company’s real website, and flags the ones that shut down or got acquired.',
  },
  {
    icon: 'search',
    title: 'Competitor customer discovery',
    comingSoon: true,
    body: 'Give us a competitor’s URL. Get back companies showing public evidence of using them, with the evidence.',
  },
  {
    icon: 'signal',
    title: 'Buying-signal hunts',
    comingSoon: true,
    body: 'Target companies that are hiring, expanding, or in the news: signals that they’re ready to buy.',
  },
  {
    icon: 'penMessage',
    title: 'AI email drafting',
    body: 'Every contact can become a researched, personalized email in one message. Revise it conversationally: “shorter,” “mention the Series B.”',
  },
];

export default function WorkflowGrid() {
  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.grid}>
          {WORKFLOWS.map((w) => (
            <article key={w.title} className={`${styles.card} ${w.comingSoon ? styles.comingSoon : ''}`}>
              <div className={styles.cardTop}>
                <span className={styles.icon} aria-hidden="true">{ICONS[w.icon]}</span>
                {w.comingSoon && <span className={styles.soon}>Coming soon</span>}
              </div>
              <h2 className={styles.title}>{w.title}</h2>
              <p className={styles.body}>{w.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
